import {
	ChevronLeftIcon,
	ChevronRightIcon,
	PlayIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useChatActions } from "../../../contexts/chat-actions-context";
import { useUserType } from "../../../hooks/useUserType";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import {
	resolveTaskDomain,
	TASK_DOMAIN_COLORS,
} from "../../../lib/journey/taskDomainColors";
import type { JourneyTask } from "../../../types/journey";
import { BottomSheet } from "../../ui/BottomSheet";
import styles from "./JourneyTaskDetailSheet.module.css";

const TITLE_ID = "journey-task-detail-sheet-title";

interface JourneyTaskDetailSheetProps {
	isOpen: boolean;
	onClose: () => void;
	task: JourneyTask | null;
	canPrev?: boolean;
	canNext?: boolean;
	onPrev?: () => void;
	onNext?: () => void;
	/** When provided, the footer shows "Mark Completed" (instead of "Go to Task"). */
	onMarkComplete?: () => void;
	markCompleteLabel?: string;
}

export function JourneyTaskDetailSheet({
	isOpen,
	onClose,
	task,
	canPrev = false,
	canNext = false,
	onPrev,
	onNext,
	onMarkComplete,
	markCompleteLabel,
}: JourneyTaskDetailSheetProps) {
	const { t } = useClientTranslations("track");
	const navigate = useNavigate();
	const { setPrefillQuestion } = useChatActions();
	const { logTrack } = useAnalytics();
	const { isD2C } = useUserType();

	if (!task) {
		return (
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				ariaLabel={t("taskDetail.close")}
				panelHeight="tall"
			>
				<div />
			</BottomSheet>
		);
	}

	const domain = resolveTaskDomain(task.taskDomain);
	const domainColor = TASK_DOMAIN_COLORS[domain];

	const prepTips: string[] = Array.isArray(task.prepTips)
		? task.prepTips.filter((x): x is string => typeof x === "string")
		: [];

	function handleAskEve() {
		logTrack("track_task_ask_eve_tapped", { taskId: task?.id });
		setPrefillQuestion(task?.title ?? "");
		navigate("/chat");
	}

	function handleGoToTask() {
		logTrack("track_task_go_to_task_tapped", { taskId: task?.id });
		onClose();
		navigate(`/get-ready?taskId=${encodeURIComponent(task?.id ?? "")}`);
	}

	function handleEducationHub() {
		logTrack("track_task_education_hub_tapped", {
			taskId: task?.id,
		});
		navigate("/education");
	}

	const sheetHeader = (
		<div
			className={styles.headerRow}
			style={{ "--domain-color": domainColor } as CSSProperties}
		>
			<button
				type="button"
				className={styles.navBtn}
				aria-label={t("sheet.prevEvent")}
				onClick={onPrev}
				disabled={!canPrev}
				aria-disabled={!canPrev}
			>
				<ChevronLeftIcon className={styles.navIcon} />
			</button>
			<h2 className={styles.title} id={TITLE_ID}>
				{task.title}
			</h2>
			<button
				type="button"
				className={styles.navBtn}
				aria-label={t("sheet.nextEvent")}
				onClick={onNext}
				disabled={!canNext}
				aria-disabled={!canNext}
			>
				<ChevronRightIcon className={styles.navIcon} />
			</button>
			<button
				type="button"
				className={styles.closeBtn}
				aria-label={t("taskDetail.close")}
				onClick={onClose}
			>
				<XMarkIcon className={styles.closeIcon} />
			</button>
		</div>
	);

	const sheetFooter = (
		<div className={styles.footerRow}>
			<button
				type="button"
				className={`${styles.footerBtn} ${styles.askEveBtn}`}
				onClick={handleAskEve}
			>
				<img
					src="/images/bottom-black-nav-chat.svg"
					alt=""
					className={styles.footerBtnIcon}
					aria-hidden="true"
				/>
				<span>{t("taskDetail.askEve")}</span>
			</button>
			{onMarkComplete ? (
				<button
					type="button"
					className={`${styles.footerBtn} ${styles.goToTaskBtn}`}
					onClick={onMarkComplete}
				>
					<span>{markCompleteLabel ?? t("taskDetail.goToTask")}</span>
				</button>
			) : (
				<button
					type="button"
					className={`${styles.footerBtn} ${styles.goToTaskBtn}`}
					onClick={handleGoToTask}
				>
					<span>{t("taskDetail.goToTask")}</span>
				</button>
			)}
		</div>
	);

	return (
		<BottomSheet
			isOpen={isOpen}
			onClose={onClose}
			ariaLabelledBy={TITLE_ID}
			header={sheetHeader}
			footer={sheetFooter}
			panelHeight="tall"
		>
			<div className={styles.body}>
				{/* Colored summary card */}
				<section
					className={styles.summaryCard}
					style={{ "--domain-color": domainColor } as CSSProperties}
				>
					<div className={styles.summaryHeader}>
						{/* 86ey11hyz QA — removed the static play-button circle; it read as
						    a video control and is irrelevant for non-video tasks. The
						    domain color still shows on the summary card itself. */}
						<div className={styles.summaryTextStack}>
							<h3 className={styles.summaryTitle}>{task.title}</h3>
							{/* dueDateLabel is the localized display string; deadline is the
							    English machine key the CDN keeps identical across locales. */}
							{(task.dueDateLabel || task.deadline) && (
								<p className={styles.deadline}>
									{task.dueDateLabel || task.deadline}
								</p>
							)}
						</div>
					</div>
				</section>

				{/* About this task */}
				{task.description && (
					<section className={styles.aboutBlock}>
						<h3 className={styles.sectionTitle}>{t("taskDetail.about")}</h3>
						<p className={styles.aboutBody}>{task.description}</p>
					</section>
				)}

				{/* Preparation tips */}
				{prepTips.length > 0 && (
					<section className={styles.tipsBlock}>
						<h3 className={styles.sectionTitle}>{t("taskDetail.tips")}</h3>
						<ul className={styles.tipsList}>
							{prepTips.map((tip) => (
								<li key={tip} className={styles.tipItem}>
									<span
										className={styles.tipBullet}
										style={{ "--domain-color": domainColor } as CSSProperties}
										aria-hidden="true"
									/>
									<span className={styles.tipText}>{tip}</span>
								</li>
							))}
						</ul>
					</section>
				)}

				{/* Education hub card — hidden for D2C (no "suggested video"). */}
				{!isD2C && (
					<button
						type="button"
						className={styles.eduCard}
						style={{ "--domain-color": domainColor } as CSSProperties}
						onClick={handleEducationHub}
						aria-label={t("taskDetail.educationHub.title")}
					>
						<span className={styles.eduIconCircle} aria-hidden="true">
							<PlayIcon className={styles.eduIcon} aria-hidden="true" />
						</span>
						<span className={styles.eduText}>
							<span className={styles.sectionTitle}>
								{t("taskDetail.educationHub.title")}
							</span>
							<span className={styles.eduSubtext}>
								{t("taskDetail.educationHub.subtext")}
							</span>
						</span>
						<ChevronRightIcon
							className={styles.eduChevron}
							aria-hidden="true"
						/>
					</button>
				)}
			</div>
		</BottomSheet>
	);
}
